import {
  useDeleteBalanceAnchorMutation,
  useDeleteBalanceReadingMutation,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {SystemProps} from 'src/Modals';

type Snapshot = {
  _id: string;
  date: string;
  /** 'plaid' rows are synced readings; anything else is a user anchor. */
  source: string;
};

export type DeleteSnapshotModalUserProps = {
  snapshot: Snapshot;
  onDeleted?: () => void;
};

const formatDateString = (isoDate: string): string => {
  return parseDate(isoDate).toLocaleString({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const DeleteSnapshotModal = (
  props: DeleteSnapshotModalUserProps & SystemProps,
) => {
  const {snapshot, onRequestClose} = props;
  const [deleteAnchor, {loading: deletingAnchor}] =
    useDeleteBalanceAnchorMutation();
  const [deleteReading, {loading: deletingReading}] =
    useDeleteBalanceReadingMutation();
  const loading = deletingAnchor || deletingReading;

  const handleDelete = (event: React.FormEvent) => {
    event.preventDefault();
    const remove =
      snapshot.source === 'plaid'
        ? deleteReading({variables: {readingId: snapshot._id}})
        : deleteAnchor({variables: {anchorId: snapshot._id}});
    void remove.then(() => {
      props.onDeleted?.();
      onRequestClose();
    });
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        type="submit"
        onClick={handleDelete}
        disabled={loading}
      >
        Delete snapshot
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Delete snapshot"
      footer={footer}
    >
      <p>
        Are you sure you want to delete the snapshot for{' '}
        <strong>{formatDateString(snapshot.date)}</strong>? This action cannot be
        undone.
      </p>
    </Modal>
  );
};
